import { useEffect } from 'react';

/**
 * Cyclic previous/next navigation with arrow key support.
 *
 * @param {Object}   options
 * @param {number}   options.index        The current index.
 * @param {number}   options.total        The number of items.
 * @param {(index: number) => void} options.onNavigate Called with the new index.
 * @param {import('react').RefObject<HTMLElement | null>} [options.scrollRef] Ref to scroll to the top on navigation.
 * @param {() => boolean} [options.isDisabled] Returns true if keyboard navigation should be ignored.
 */
export const useCyclicNavigation = ({ index, total, onNavigate, scrollRef, isDisabled }) => {
	const hasNavigation = total > 1;
	const previousIndex = (index - 1 + total) % total;
	const nextIndex = (index + 1) % total;

	useEffect(() => {
		scrollRef?.current?.scrollTo({ top: 0 });
	}, [index]);

	const goToPrevious = () => onNavigate(previousIndex);
	const goToNext = () => onNavigate(nextIndex);

	/** @param {import('react').KeyboardEvent<HTMLDialogElement>} event */
	const handleKeyDown = (event) => {
		if (!hasNavigation || isDisabled?.()) {
			return;
		}

		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			goToPrevious();
		} else if (event.key === 'ArrowRight') {
			event.preventDefault();
			goToNext();
		}
	};

	return { hasNavigation, goToPrevious, goToNext, handleKeyDown };
};
